import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ContentCard } from "@/components/ui/ContentCard";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Settings, Save, Trash2, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export interface OffenseTierConfig {
  id: string;
  label: string;
  description: string;
  examples: string;
}

export interface DistrictConfiguration {
  districtName: string;
  tierDefinitions: OffenseTierConfig[];
  disciplineFormTemplate: string;
  incidentCategories: string[];
  sisSystem: string;
  customFields: string;
}

const DEFAULT_CONFIG: DistrictConfiguration = {
  districtName: "",
  tierDefinitions: [
    { id: "minor", label: "Minor / Tier 1", description: "Classroom-managed behavior", examples: "Disruption, off-task, dress code" },
    { id: "major", label: "Major / Tier 2", description: "Office-managed behavior requiring admin intervention", examples: "Repeated minor offenses, defiance, bullying" },
    { id: "severe", label: "Severe / Tier 3", description: "Serious safety concern", examples: "Fighting, weapons, substance possession" },
  ],
  disciplineFormTemplate: "",
  incidentCategories: [],
  sisSystem: "",
  customFields: "",
};

const CONFIG_STORAGE_PREFIX = "restoration_room_district_config_v2:";
const LEGACY_CONFIG_STORAGE_KEY = "restoration_room_district_config_v1";

function configStorageKey(userId: string): string {
  return `${CONFIG_STORAGE_PREFIX}${userId}`;
}

function readConfig(userId: string | null | undefined): DistrictConfiguration {
  if (!userId) return DEFAULT_CONFIG;
  try {
    const stored = localStorage.getItem(configStorageKey(userId));
    return stored ? { ...DEFAULT_CONFIG, ...JSON.parse(stored) } : DEFAULT_CONFIG;
  } catch {
    return DEFAULT_CONFIG;
  }
}

export const useDistrictConfig = (
  userId: string | null | undefined,
): [DistrictConfiguration, (config: DistrictConfiguration) => void] => {
  const [config, setConfig] = useState<DistrictConfiguration>(() => readConfig(userId));

  useEffect(() => {
    setConfig(readConfig(userId));
  }, [userId]);

  const saveConfig = useCallback(
    (newConfig: DistrictConfiguration) => {
      setConfig(newConfig);
      if (!userId) return;
      try {
        localStorage.setItem(configStorageKey(userId), JSON.stringify(newConfig));
        localStorage.removeItem(LEGACY_CONFIG_STORAGE_KEY);
      } catch (e) {
        console.error("Failed to save config", e);
      }
    },
    [userId],
  );

  return [config, saveConfig];
};

interface DistrictConfigProps {
  config: DistrictConfiguration;
  onSave: (config: DistrictConfiguration) => void;
  onClose: () => void;
}

export const DistrictConfigPanel = ({ config, onSave, onClose }: DistrictConfigProps) => {
  const [draft, setDraft] = useState<DistrictConfiguration>({ ...config });
  const [expandedTier, setExpandedTier] = useState<string | null>(null);

  const updateDraft = (fields: Partial<DistrictConfiguration>) => {
    setDraft((prev) => ({ ...prev, ...fields }));
  };

  const updateTier = (id: string, fields: Partial<OffenseTierConfig>) => {
    setDraft((prev) => ({
      ...prev,
      tierDefinitions: prev.tierDefinitions.map((t) =>
        t.id === id ? { ...t, ...fields } : t
      ),
    }));
  };

  const addTier = () => {
    const newId = `tier-${Date.now()}`;
    setDraft((prev) => ({
      ...prev,
      tierDefinitions: [
        ...prev.tierDefinitions,
        { id: newId, label: "New Tier", description: "", examples: "" },
      ],
    }));
    setExpandedTier(newId);
  };

  const removeTier = (id: string) => {
    setDraft((prev) => ({
      ...prev,
      tierDefinitions: prev.tierDefinitions.filter((t) => t.id !== id),
    }));
  };

  const handleSave = () => {
    onSave(draft);
    toast({ title: "District configuration saved" });
    onClose();
  };

  return (
    <ContentCard className="border-2 border-bodhi-gold/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-bodhi-gold" />
          <h2 className="text-lg font-semibold text-gray-800 font-literata">District Configuration</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-xs text-gray-400">
          Close
        </Button>
      </div>

      <p className="text-sm text-gray-600 mb-5">
        Customize definitions, categories, and output formats to match your district's discipline framework.
        These settings apply to all incident documentation.
      </p>

      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>District / school name</Label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bodhi-teal"
              placeholder="e.g. Springfield USD"
              value={draft.districtName}
              onChange={(e) => updateDraft({ districtName: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>SIS system</Label>
            <input
              type="text"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bodhi-teal"
              placeholder="e.g. Infinite Campus, Synergy, PowerSchool"
              value={draft.sisSystem}
              onChange={(e) => updateDraft({ sisSystem: e.target.value })}
            />
          </div>
        </div>

        {/* Tier definitions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Offense tier definitions</Label>
            <Button variant="ghost" size="sm" onClick={addTier} className="text-xs text-bodhi-teal">
              <Plus className="w-3 h-3 mr-1" /> Add Tier
            </Button>
          </div>
          <div className="space-y-2">
            {draft.tierDefinitions.map((tier) => {
              const isExpanded = expandedTier === tier.id;
              return (
                <div key={tier.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedTier(isExpanded ? null : tier.id)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-700">{tier.label}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-bodhi-teal"
                        placeholder="Tier label (e.g. Minor / Tier 1)"
                        value={tier.label}
                        onChange={(e) => updateTier(tier.id, { label: e.target.value })}
                      />
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-bodhi-teal"
                        placeholder="Description"
                        value={tier.description}
                        onChange={(e) => updateTier(tier.id, { description: e.target.value })}
                      />
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-bodhi-teal"
                        placeholder="Examples (comma-separated)"
                        value={tier.examples}
                        onChange={(e) => updateTier(tier.id, { examples: e.target.value })}
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeTier(tier.id)} className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-3 h-3 mr-1" /> Remove tier
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Discipline form template */}
        <div className="space-y-1.5">
          <Label>Discipline form template (optional)</Label>
          <p className="text-xs text-gray-500">
            Paste your district's discipline report form template. HeyBodhi will use this to format output in your local format.
          </p>
          <Textarea
            placeholder="Paste your discipline form template here..."
            rows={4}
            value={draft.disciplineFormTemplate}
            onChange={(e) => updateDraft({ disciplineFormTemplate: e.target.value })}
          />
        </div>

        {/* Incident categories */}
        <div className="space-y-1.5">
          <Label>Incident categories (optional)</Label>
          <p className="text-xs text-gray-500">
            Enter your district's incident categories, one per line (e.g. Disruption, Fighting, Bullying, Insubordination).
          </p>
          <Textarea
            placeholder="Disruption&#10;Fighting&#10;Bullying&#10;Insubordination&#10;..."
            rows={4}
            value={draft.incidentCategories.join("\n")}
            onChange={(e) => updateDraft({
              incidentCategories: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
            })}
          />
        </div>

        {/* Custom fields */}
        <div className="space-y-1.5">
          <Label>Additional notes / custom fields</Label>
          <Textarea
            placeholder="Any other information you want included in documentation output..."
            rows={3}
            value={draft.customFields}
            onChange={(e) => updateDraft({ customFields: e.target.value })}
          />
        </div>
      </div>

      <div className="flex gap-2 mt-5 pt-4 border-t border-gray-200">
        <Button onClick={handleSave} className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90">
          <Save className="w-4 h-4 mr-2" /> Save Configuration
        </Button>
        <Button variant="outline" onClick={onClose} className="border-gray-300 text-gray-700 hover:bg-gray-100">
          Cancel
        </Button>
      </div>
    </ContentCard>
  );
};
